import { useTranslation } from "@sagot/i18n";
import { cn, Icon } from "@sagot/ui";
import { effectiveFallback, type ExitFallback, type ExitRef, type ExitSlot } from "../exit-slots";
import { CanvasHandle } from "./canvas-handle";
import { useConnectors } from "./connector-context";

const K = "engage.workflows.connectors";
const CHIP = "max-w-[55%] shrink-0 truncate rounded-pill border px-1.5 text-[10px] font-bold";

interface ExitRowProps {
  source: ExitRef;
  slot: ExitSlot;
  stepName: string;
  /** The exit sits on the last step of its path, so "carry on with the next step" means the workflow ends. */
  lastStep: boolean;
}

/** Where an exit leads: its path, or what happens instead while it is not connected. */
function ExitTarget({ slot, fallback }: { slot: ExitSlot; fallback: ExitFallback }) {
  const { t } = useTranslation();
  const api = useConnectors();
  if (slot.target && api.hasPath(slot.target)) {
    return <span className={cn(CHIP, "border-line bg-surface text-primary-ink")}>{t(`${K}.to`, { path: api.pathChip(slot.target) })}</span>;
  }
  if (slot.target || fallback === "unset") {
    return (
      <span className={cn(CHIP, "inline-flex items-center gap-1 border-warning bg-warning-soft text-warning")}>
        <Icon name="alert-triangle" className="size-3 shrink-0" />
        <span className="truncate">{slot.target ? t(`${K}.missingPath`, { id: slot.target }) : t(`${K}.fallback.unset`)}</span>
      </span>
    );
  }
  return <span className="shrink-0 text-[10px] text-fg-subtle">{t(`${K}.fallback.${fallback}`)}</span>;
}

/** An exit that always goes to the same path (Start over): named and drawn, with no handle to connect, move or delete. */
function FixedExitRow({ source, slot }: { source: ExitRef; slot: ExitSlot }) {
  const { t } = useTranslation();
  const api = useConnectors();
  return (
    <li className="relative flex h-[var(--geo-exit)] items-center gap-1.5 pl-[var(--geo-indent)] pr-3" data-testid="canvas-exit" data-exit={slot.key} data-fixed="true">
      <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[11px]">
        <span className="min-w-0 truncate font-semibold text-fg-muted">{api.exitName(source)}</span>
        <span className={cn(CHIP, "border-line bg-surface text-primary-ink")}>{t(`${K}.to`, { path: api.pathChip(slot.target ?? "") })}</span>
        <span className="sr-only">{t(`${K}.fixedExit`)}</span>
      </span>
      <span aria-hidden="true" className="absolute right-[var(--geo-handle-x)] top-1/2 flex size-[var(--geo-handle)] -translate-y-1/2 items-center justify-center">
        <span className="block size-2 rounded-pill bg-fg-subtle" />
      </span>
    </li>
  );
}

/** One exit of a step, indented under it: its name, where it leads and the handle to connect it. */
export function ExitRow({ source, slot, stepName, lastStep }: ExitRowProps) {
  const api = useConnectors();
  if (slot.fixed) return <FixedExitRow source={source} slot={slot} />;
  const fallback = effectiveFallback(slot, lastStep);
  const connected = slot.target !== undefined && api.hasPath(slot.target);
  // A viewer gets a handle only where there is a path to jump to.
  const interactive = api.editable || connected;
  return (
    <li className="relative flex h-[var(--geo-exit)] items-center gap-1.5 pl-[var(--geo-indent)] pr-3" data-testid="canvas-exit" data-exit={slot.key}>
      <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[11px]" aria-hidden={interactive ? true : undefined}>
        <span className="min-w-0 truncate font-semibold text-fg-muted">{api.exitName(source)}</span>
        <ExitTarget slot={slot} fallback={fallback} />
      </span>
      {interactive ? (
        <CanvasHandle source={source} slot={slot} stepName={stepName} fallback={fallback} />
      ) : (
        <span aria-hidden="true" className="absolute right-[var(--geo-handle-x)] top-1/2 flex size-[var(--geo-handle)] -translate-y-1/2 items-center justify-center">
          <span className="block size-3 rounded-pill border-2 border-line-strong bg-surface" />
        </span>
      )}
    </li>
  );
}
